"use client"

import type { ReactNode } from "react"
import { Form, Formik } from "formik"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { FormChannelSelect, FormSwitch } from "@/components/form-inputs"
import type { SelectableChannel } from "./channel-select"
import { DestinationList } from "./destination-list"
import { buildDestinations, type DestinationCandidate } from "./destinations"
import { useTranslations } from "next-intl"


export interface EventSubscriptionValues {
  enabled: boolean
  channelIds: string[]
}

interface EventSubscriptionCardProps {
  title: string
  description: ReactNode
  /** Saved state: the destinations list where it goes now, not the pending selection. */
  subscription: EventSubscriptionValues
  options: SelectableChannel[]
  candidates: DestinationCandidate[]
  /** What an empty selection means. */
  emptyLabel: string
  /** Said instead of the destinations when not even the system channel can deliver. */
  noSystemChannelNote: string
  onSave: (values: EventSubscriptionValues) => Promise<unknown>
  saving: boolean
  optionsLoading?: boolean
  /** Why nothing here can change right now; the controls lock while it is set. */
  lockedNotice?: ReactNode
  actions?: ReactNode
}

export function EventSubscriptionCard({
  title,
  description,
  subscription,
  options,
  candidates,
  emptyLabel,
  noSystemChannelNote,
  onSave,
  saving,
  optionsLoading,
  lockedNotice,
  actions,
}: EventSubscriptionCardProps) {
  const tUi = useTranslations("NotificationChannels.NotificationChannelsEventSubscriptionCard")

  const { destinations, systemChannelMissing } = buildDestinations(candidates, subscription.channelIds)
  const locked = Boolean(lockedNotice)

  return (
    <Card>
      <CardHeader className="gap-1 pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-base">{title}</CardTitle>
          {!subscription.enabled && <Badge variant="outline"><span key={tUi("off")}>{tUi("off")}</span></Badge>}
        </div>
        <div className="text-sm text-muted-foreground">{description}</div>
      </CardHeader>
      <CardContent className="space-y-3">
        {lockedNotice && (
          <Alert>
            <AlertDescription>{lockedNotice}</AlertDescription>
          </Alert>
        )}

        <Formik
          enableReinitialize
          initialValues={subscription}
          // Must return a promise: on a sync handler Formik leaves `isSubmitting` latched,
          // and a failed save would disable Save until the card remounts.
          onSubmit={(values) => onSave(values)}
        >
          {({ values, dirty, isSubmitting }) => (
            <Form className="space-y-3">
              <FormSwitch name="enabled" label={tUi("sendThisNotification")} disabled={saving || locked} />

              <div className="flex flex-wrap items-center gap-2">
                <FormChannelSelect
                  name="channelIds"
                  options={options}
                  emptyLabel={emptyLabel}
                  disabled={saving || locked || !values.enabled}
                  loading={optionsLoading}
                />
                <Button type="submit" size="sm" disabled={saving || locked || isSubmitting || !dirty}>
                  <span key={tUi("save")}>{tUi("save")}</span></Button>
              </div>
            </Form>
          )}
        </Formik>

        {subscription.enabled && (
          <DestinationList
            destinations={destinations}
            emptyFallbackNote={systemChannelMissing ? noSystemChannelNote : undefined}
          />
        )}

        {actions}
      </CardContent>
    </Card>
  )
}
